import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AdminVideo } from '../../../common/model/dto/video/admin-video';
import { VideoUploadItem } from '../../../context/video-upload.types';
import { getVideoStatusDisplay } from '../../../common/utils/video-status-utils';
import OvStatusChip from '../atoms/OvStatusChip';
import OvVideoUploadProgress from '../atoms/OvVideoUploadProgress';
import Variables from '../../../design-system/variables';

interface OvVideoStatusChipProps {
  video: AdminVideo;
  upload?: VideoUploadItem;
}

const OvVideoStatusChip: FC<OvVideoStatusChipProps> = ({ video, upload }) => {
  const { t } = useTranslation();
  const display = getVideoStatusDisplay(video, upload);
  const progressLabel = t('videos.uploads.progressLabel', {
    title: video.title,
  });

  switch (display.kind) {
    case 'status':
      return (
        <OvStatusChip
          label={t(`videos.status.${display.status}`)}
          tone={display.tone}
        />
      );

    case 'queued':
      return (
        <Progress>
          <span>{t('videos.uploads.queued')}</span>
          <OvVideoUploadProgress label={progressLabel} percent={0} />
        </Progress>
      );

    case 'uploading':
      return (
        <Progress>
          <span>
            {t('videos.status.uploadingPercent', { percent: display.percent })}
          </span>
          <OvVideoUploadProgress
            label={progressLabel}
            percent={display.percent}
          />
        </Progress>
      );

    case 'finishing':
      return (
        <Progress>
          <span>{t('videos.uploads.completing')}</span>
          <OvVideoUploadProgress label={progressLabel} />
        </Progress>
      );

    case 'uploadFailed':
      return (
        <OvStatusChip
          label={t('videos.status.uploadFailed')}
          tone={display.tone}
        />
      );

    case 'uploadNotFinished':
      return (
        <OvStatusChip
          label={t('videos.status.uploadNotFinished')}
          tone={display.tone}
        />
      );

    default: {
      const unhandledDisplay: never = display;

      return unhandledDisplay;
    }
  }
};

export default OvVideoStatusChip;

const Progress = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 7rem;
  font-size: ${Variables.fontSizes.SMALL};
`;
